export type LiftTypeGroup = "drags" | "gondolas" | "chairs";

export type AllowedLiftTypes = Record<LiftTypeGroup, boolean>;

export const ALL_LIFT_GROUPS_ALLOWED: AllowedLiftTypes = {
  drags: true,
  gondolas: true,
  chairs: true,
};

/** Fleet-median effective vertical rate from the tracked ski graph fixtures. */
export const VPM_REF = 22.8;
export const VPM_WEIGHT_MIN = 0.6;
export const VPM_WEIGHT_MAX = 1.6;

const LIFT_TYPES_BY_GROUP: Record<LiftTypeGroup, readonly string[]> = {
  drags: [
    "drag_lift",
    "t-bar",
    "j-bar",
    "platter",
    "rope_tow",
    "magic_carpet",
  ],
  gondolas: [
    "gondola",
    "cable_car",
    "funicular",
    "mixed_lift",
    "railway",
  ],
  chairs: ["chair_lift"],
};

const GROUP_BY_LIFT_TYPE = new Map<string, LiftTypeGroup>(
  (Object.entries(LIFT_TYPES_BY_GROUP) as [LiftTypeGroup, readonly string[]][])
    .flatMap(([group, types]) => types.map((type) => [type, group] as const)),
);

/** Unknown types deliberately return null because they cannot be banned safely. */
export function liftTypeGroup(liftType: string): LiftTypeGroup | null {
  return GROUP_BY_LIFT_TYPE.get(liftType) ?? null;
}

/** Resolve disabled UI groups to the concrete OSM lift types used by the engine. */
export function bannedLiftTypes(
  allowedTypes?: AllowedLiftTypes,
): Set<string> {
  if (!allowedTypes) return new Set();
  const banned = new Set<string>();
  for (const group of Object.keys(LIFT_TYPES_BY_GROUP) as LiftTypeGroup[]) {
    if (allowedTypes[group]) continue;
    for (const liftType of LIFT_TYPES_BY_GROUP[group]) banned.add(liftType);
  }
  return banned;
}

export type LiftVerticalRateInputs = {
  bottomElevationM: number;
  topElevationM: number;
  durationS: number;
  waitMinutes: number;
};

/** Honest uplift rate, including the estimated wait before boarding. */
export function effectiveLiftVerticalRate(
  inputs: LiftVerticalRateInputs,
): number {
  const minutes = inputs.durationS / 60 + Math.max(0, inputs.waitMinutes);
  if (minutes <= 0) return 0;
  return Math.max(0, inputs.topElevationM - inputs.bottomElevationM) / minutes;
}

/** Search-only access weight: quicker uplift looks cheaper, slow uplift dearer. */
export function fastLiftAccessWeight(vpm: number): number {
  return Math.max(
    VPM_WEIGHT_MIN,
    Math.min(VPM_WEIGHT_MAX, VPM_REF / Math.max(vpm, 1)),
  );
}

export function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
}

export function updateAllowedLiftTypes(
  current: AllowedLiftTypes | undefined,
  group: LiftTypeGroup,
  allowed: boolean,
): { allowedTypes: AllowedLiftTypes; rejected: boolean } {
  const previous = { ...ALL_LIFT_GROUPS_ALLOWED, ...(current ?? {}) };
  const next = { ...previous, [group]: allowed };
  if (!Object.values(next).some(Boolean)) {
    return { allowedTypes: previous, rejected: true };
  }
  return { allowedTypes: next, rejected: false };
}
